import { FC, useEffect, useRef, useState, useCallback, DragEvent } from 'react'
import { Stethoscope, FileUp } from 'lucide-react'
import { ChatInput } from './ChatInput'
import { ChatMessages } from './ChatMessages'
import { ChatScrollButtons } from './ChatScrollButtons'
import { ArtifactPanel } from './ArtifactPanel'
import { DocumentsStrip } from './DocumentsStrip'
import { ThinkingSidePanel } from './ThinkingSidePanel'
import { PatientContextHeader } from './PatientContextHeader'
import { DocumentUploadZone } from './DocumentUploadZone'
import { useScroll } from './hooks/useScroll'
import { useChat } from '../../contexts/ChatContext'
import { cn } from '../../lib/utils'

interface ExpandedThinking {
  content: string
  durationMs?: number
  isStreaming?: boolean
}

interface ChatUIProps {
  patientId?: string
  patientName?: string
  userFirstName?: string
  className?: string
}

/**
 * Main chat interface component - Clinical Intelligence System design
 * Layout: Documents top → Welcome/Messages middle → Input fixed bottom
 */
export const ChatUI: FC<ChatUIProps> = ({ patientId, patientName, userFirstName, className = '' }) => {
  const {
    expandedArtifact,
    expandArtifact,
    messages,
    setPatient,
    pendingMessage,
    setPendingMessage,
    sendMessage,
    streamingProgress,
    isStreaming,
    isGenerating,
    // Patient Data
    patientData,
    clearPatientData,
    setShowPatientDataInput,
    reconcilePatientData,
    isReconciling,
    patientPanelMode,
    togglePatientPanelMode,
    // Unified Files
    files,
    addFile,
    removeFile,
    // Conversation ID
    currentConversationId: _currentConversationId,
  } = useChat()

  const [expandedThinking, setExpandedThinking] = useState<ExpandedThinking | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Callback to expand thinking content to side panel
  const handleExpandThinking = useCallback((content: string, durationMs?: number) => {
    setExpandedThinking({
      content,
      durationMs,
      isStreaming: streamingProgress.isThinking
    })
  }, [streamingProgress.isThinking])

  // Close thinking panel
  const handleCloseThinking = useCallback(() => {
    setExpandedThinking(null)
  }, [])

  const {
    messagesStartRef,
    messagesEndRef,
    handleScroll,
    scrollToBottom,
    scrollToBottomSmooth,
    setIsAtBottom,
    setUserScrolled,
    isAtTop,
    isAtBottom,
    isOverflowing,
    userScrolled,
    scrollToTop
  } = useScroll()

  // Set patient context from workspace props
  useEffect(() => {
    if (patientId && patientName && patientId !== 'default') {
      setPatient({ id: patientId, name: patientName })
    } else if (patientId === 'default') {
      setPatient(null)
    }
  }, [patientId, patientName, setPatient])

  useEffect(() => {
    scrollToBottom()
    setIsAtBottom(true)
  }, [scrollToBottom, setIsAtBottom])

  // Auto-send pending message from project view
  const pendingMessageSentRef = useRef<string | null>(null)
  useEffect(() => {
    if (pendingMessage && pendingMessageSentRef.current !== pendingMessage.message) {
      pendingMessageSentRef.current = pendingMessage.message
      sendMessage(pendingMessage.message, pendingMessage.projectId)
      setPendingMessage(null)
    }
  }, [pendingMessage, sendMessage, setPendingMessage])

  // Track previous message count to detect new messages
  const prevMessageCount = useRef(messages.length)

  // Auto-scroll when user sends a new message
  useEffect(() => {
    if (messages.length > prevMessageCount.current) {
      const lastMessage = messages[messages.length - 1]
      if (lastMessage?.role === 'user') {
        setUserScrolled(false)
        scrollToBottom()
      }
    }
    prevMessageCount.current = messages.length
  }, [messages.length, messages, scrollToBottom, setUserScrolled])

  // Auto-scroll during streaming
  useEffect(() => {
    if ((isStreaming || isGenerating) && !userScrolled) {
      scrollToBottomSmooth()
    }
  }, [isStreaming, isGenerating, userScrolled, scrollToBottomSmooth, messages])

  // File handling
  const handleFileSelect = useCallback((file: File) => {
    const fileName = file.name.toLowerCase()
    const validExtensions = ['.txt', '.md', '.xml', '.cda', '.pdf', '.csv', '.jpg', '.jpeg', '.png']
    const isValid = validExtensions.some(ext => fileName.endsWith(ext))

    if (!isValid || file.size > 10 * 1024 * 1024) return
    addFile(file, 'dropzone')
  }, [addFile])

  const handleDrop = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragOver(false)
    const file = e.dataTransfer.files?.[0]
    if (file) handleFileSelect(file)
  }, [handleFileSelect])

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragOver(false)
  }, [])

  const hasMessages = messages.length > 0
  const hasDocuments = files.length > 0

  return (
    <div className={cn("relative flex h-full overflow-hidden", className)}>
      {/* Main Chat Area */}
      <div className={cn(
        "relative flex flex-1 min-w-0 flex-col transition-all duration-300 overflow-x-clip",
        hasMessages ? "items-stretch" : "items-center justify-center"
      )}>

        {/* Patient Context Header - Shows in collapsed (header) mode */}
        {hasMessages && (patientData || files.length > 0) && patientPanelMode !== 'column' && (
          <PatientContextHeader
            data={patientData}
            onEdit={() => setShowPatientDataInput(true)}
            onClear={clearPatientData}
            onReconcile={reconcilePatientData}
            isReconciling={isReconciling}
            uploadedDocuments={files}
            onExpandToColumn={togglePatientPanelMode}
          />
        )}

        {/* Document Upload Zone - Shows when documents are uploaded (before or during chat) */}
        {hasDocuments && !hasMessages && (
          <DocumentUploadZone
            documents={files}
            onAddFile={(file) => addFile(file, 'dropzone')}
            onRemoveDocument={removeFile}
          />
        )}

        {/* Top bar: Scroll Buttons (left) + Scribe Button (right) */}
        <div className="absolute top-3 left-4 right-4 z-10 flex items-center justify-between pointer-events-none">
          {hasMessages ? (
            <div className="pointer-events-auto">
              <ChatScrollButtons
                isAtTop={isAtTop}
                isAtBottom={isAtBottom}
                isOverflowing={isOverflowing}
                scrollToTop={scrollToTop}
                scrollToBottom={scrollToBottom}
              />
            </div>
          ) : <div />}
          {/* Scribe button moved to IntakePage */}
        </div>

        {/* Main Content Area - with Documents Strip */}
        <div className="relative flex-1 flex overflow-hidden">
          {/* Scrollable Messages */}
          <div
            className={cn(
              "flex flex-1 w-full flex-col overflow-auto scrollbar-hide",
              !hasMessages && "items-center justify-center",
              // Add right padding when strip is visible and canvas is closed
              !expandedArtifact && messages.some(m => m.artifact) && "pr-10"
            )}
            onScroll={hasMessages ? handleScroll : undefined}
            onDrop={!hasMessages ? handleDrop : undefined}
            onDragOver={!hasMessages ? handleDragOver : undefined}
            onDragLeave={!hasMessages ? handleDragLeave : undefined}
          >
            {hasMessages ? (
              <>
                <div ref={messagesStartRef} />
                <div className="flex flex-col items-center">
                  <div className="w-full max-w-[800px]">
                    <ChatMessages
                      patientId={patientId}
                      onExpandThinking={handleExpandThinking}
                    />
                  </div>
                </div>
                <div ref={messagesEndRef} />
              </>
            ) : (
              /* Welcome State - adapts based on whether documents exist */
              <div className={cn(
                "flex flex-col items-center text-center px-4 py-8 max-w-lg transition-all",
                isDragOver && "scale-[1.02]"
              )}>
                {/* Icon */}
                <div className="relative mb-5">
                  <div className="absolute inset-0 bg-primary/15 rounded-full blur-2xl scale-150 animate-pulse" />
                  <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20 flex items-center justify-center">
                    <Stethoscope className="h-8 w-8 text-primary" />
                  </div>
                </div>

                {/* Greeting */}
                <h2 className="text-2xl font-display font-bold tracking-tight mb-2">
                  Hi, {userFirstName || 'there'}
                </h2>
                <p className="text-muted-foreground mb-6">
                  {hasDocuments
                    ? "Your documents are being processed. Start chatting below!"
                    : "Upload patient data or start chatting below"
                  }
                </p>

                {/* Upload Zone - only show when no documents yet */}
                {!hasDocuments && (
                  <div
                    className={cn(
                      "w-full rounded-xl border-2 border-dashed p-6 transition-all cursor-pointer",
                      "hover:border-primary/50 hover:bg-primary/5",
                      isDragOver
                        ? "border-primary bg-primary/10 scale-[1.02]"
                        : "border-border/50 bg-card/30"
                    )}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                        <FileUp className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-medium">
                          Drop files here or click to upload
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          PDF, TXT, CSV, images, or CCD XML
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".txt,.md,.xml,.cda,.pdf,.csv,.jpg,.jpeg,.png,text/plain,text/markdown,application/xml,application/pdf,text/csv,image/jpeg,image/png"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) handleFileSelect(file)
                    e.target.value = ''
                  }}
                  className="hidden"
                />

                {/* Hint */}
                <p className="text-xs text-muted-foreground mt-6">
                  {hasDocuments
                    ? "Type a message to start chatting with your documents"
                    : "Or just start typing below to chat without patient context"
                  }
                </p>
              </div>
            )}
          </div>

          {/* Documents Strip - right edge, hides when canvas open */}
          <DocumentsStrip
            messages={messages}
            onDocumentClick={expandArtifact}
            isHidden={!!expandedArtifact}
          />
        </div>

        {/* Chat Input - ALWAYS visible at bottom */}
        <div className={cn(
          "relative w-full items-end px-4",
          hasMessages ? "pb-4 pt-2" : "pb-4 pt-0 sm:pb-8 sm:pt-5",
          "border-t border-border/30 bg-background/80 backdrop-blur-sm"
        )}>
          {/* Document indicator when docs exist and chatting */}
          {hasDocuments && !hasMessages && (
            <div className="max-w-[800px] mx-auto mb-2 flex items-center gap-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                {files.filter(f => f.status === 'ready').length} file(s) ready
              </span>
              <span>·</span>
              <span>Send a message to merge into patient context</span>
            </div>
          )}

          <div className="mx-auto max-w-[800px]">
            <ChatInput patientId={patientId} hidePaperclip={!hasMessages} />
          </div>
        </div>
      </div>

      {/* Artifact Panel (Side Panel) */}
      {expandedArtifact && <ArtifactPanel />}

      {/* Thinking Side Panel */}
      {expandedThinking && (
        <ThinkingSidePanel
          content={expandedThinking.content}
          durationMs={expandedThinking.durationMs}
          isStreaming={expandedThinking.isStreaming}
          onClose={handleCloseThinking}
        />
      )}

      {/* Encounter overlays moved to WorkspaceManager for global visibility */}
    </div>
  )
}
